import type {
    TradeLogicAuthoringBackendCapability,
    TradeLogicAuthoringBackendStatus,
    TradeLogicAuthoringBackendStatusInput,
    TradeLogicAuthoringOperationKey,
    TradeLogicAuthoringOperationState
} from './types';

const createAvailableCapability = (
    key: TradeLogicAuthoringOperationKey,
    label: string,
    scope: TradeLogicAuthoringBackendCapability['scope'],
    endpointHint: string
): TradeLogicAuthoringBackendCapability => ({
    key,
    label,
    scope,
    availability: 'available',
    reason: null,
    endpointHint
});

export const createTradeLogicAuthoringBackendStatus = ({
    strategyId,
    draftKind,
    registryManifestFingerprint
}: TradeLogicAuthoringBackendStatusInput): TradeLogicAuthoringBackendStatus => ({
    isReady: true,
    checkedAt: new Date().toISOString(),
    capabilities: {
        validateDraft: createAvailableCapability('validateDraft', 'Validate Draft', 'draft', 'tradeLogicAuthoring.validateDraft'),
        compileDraft: createAvailableCapability('compileDraft', 'Compile Preview', 'draft', 'tradeLogicAuthoring.compileDraft'),
        saveDraft: createAvailableCapability('saveDraft', 'Save Draft', 'draft', 'tradeLogicAuthoring.saveDraft'),
        publishDraft: createAvailableCapability('publishDraft', 'Publish Version', 'draft', 'tradeLogicAuthoring.publishDraft'),
        searchGlobalCandidates: createAvailableCapability(
            'searchGlobalCandidates',
            'Search Global Candidates',
            'candidate',
            'tradeLogicExitCandidates.searchGlobalCandidates'
        ),
        getStrategyCandidateAttachments: createAvailableCapability(
            'getStrategyCandidateAttachments',
            'Get Strategy Candidate Attachments',
            'candidate',
            'tradeLogicExitCandidates.getStrategyCandidateAttachments'
        ),
        previewCandidate: createAvailableCapability(
            'previewCandidate',
            'Preview Candidate',
            'candidate',
            'tradeLogicExitCandidates.previewExecutableCandidateDocument'
        ),
        previewBaseCandidate: createAvailableCapability(
            'previewBaseCandidate',
            'Preview Base Candidate',
            'candidate',
            'tradeLogicExitCandidates.previewBaseStrategyCandidate'
        ),
        materializeBaseCandidate: createAvailableCapability(
            'materializeBaseCandidate',
            'Materialize Base Candidate',
            'candidate',
            'tradeLogicExitCandidates.materializeBaseStrategyCandidate'
        ),
        createCandidate: createAvailableCapability(
            'createCandidate',
            'Create Global Candidate',
            'candidate',
            'tradeLogicExitCandidates.createCandidate'
        ),
        getCandidateCoverage: createAvailableCapability(
            'getCandidateCoverage',
            'Get Candidate Coverage',
            'candidateRun',
            'tradeLogicExitCandidates.getCandidateCoverage'
        ),
        getBestCandidateBacktest: createAvailableCapability(
            'getBestCandidateBacktest',
            'Get Best Candidate',
            'candidateRun',
            'tradeLogicExitCandidates.getBestCandidateBacktest'
        ),
        queueCandidateBacktests: createAvailableCapability(
            'queueCandidateBacktests',
            'Queue Candidate Backtests',
            'candidateRun',
            'tradeLogicExitCandidates.queueCandidateCoverage'
        ),
        queueAeroJob: createAvailableCapability('queueAeroJob', 'Queue AERO Job', 'candidateRun', 'exitOptimization.createJob'),
        importAeroResult: createAvailableCapability(
            'importAeroResult',
            'Preview AERO Results',
            'candidateRun',
            'exitOptimization.fetchTopResults'
        ),
        promoteCandidate: createAvailableCapability(
            'promoteCandidate',
            'Publish Global Exit Candidates',
            'candidateRun',
            'exitOptimization.promoteResultsToGlobalCandidates'
        )
    },
    metadata: {
        draftKind,
        registryManifestFingerprint,
        strategyId
    }
});

export const getTradeLogicAuthoringOperationState = (
    capability: TradeLogicAuthoringBackendCapability
): TradeLogicAuthoringOperationState => ({
    isAvailable: capability.availability === 'available',
    isPending: capability.availability === 'pendingBackend',
    reason: capability.reason
});
